import { z } from 'zod';
import { SCREENING_STATUSES } from './enums.js';
import { memberSummarySchema } from './members.js';
import { movieSchema } from './movies.js';
import { DEFAULT_TITLE_ORDER, NO_TITLE, movieTitle, titleSetSchema, } from './titles.js';
/**
 * Screenings: the wire contract for the Now Showing board and for scheduling.
 *
 * A screening is the only unit of programme on this site — no seasons, no
 * festivals (DESIGN.md §17, "what is deliberately not here"). Everything else
 * hangs off one.
 */
export const screeningStatusSchema = z.enum(SCREENING_STATUSES);
export const roomSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export const roomSchema = roomSummarySchema.extend({
    ownerId: z.string().uuid(),
    ownerNickname: z.string().nullable(),
    imageUrl: z.string().nullable(),
    /**
     * The image's own dimensions. Seat coordinates are fractions of it, not
     * pixels in it (see `seats.ts`), so these are here to give the map its
     * aspect ratio before the image loads — without them the markers lay out
     * against a zero-height box and jump when it arrives.
     */
    imageW: z.number().int().nullable(),
    imageH: z.number().int().nullable(),
    notes: z.string().nullable(),
    isActive: z.boolean(),
    /** Bookable seats placed so far. Zero until somebody draws the room's map. */
    seatCount: z.number().int().nonnegative(),
});
/**
 * How long a screening stays on the board after it starts.
 *
 * "Past screenings drop off the board" (DESIGN.md §7) cannot mean the moment
 * the clock passes 21:00 — the card is at its most useful *during* the film,
 * when somebody is running late and wants the address. So a screening lives
 * until roughly when the credits roll.
 */
export const BOARD_GRACE_MIN = 30;
/** Used when the movie has no runtime, and for wildcard slots with no movie. */
export const ASSUMED_RUNTIME_MIN = 120;
/**
 * The statuses that put a screening in front of people.
 *
 * One list, exported, because three separate places need to agree on it: the
 * board's `WHERE` clause, the decision to broadcast when a screening becomes
 * visible, and whether booking is open. An earlier version of the second one
 * compared against `announced` alone, and opening a wildcard slot appeared on
 * nobody's screen until they reloaded.
 */
export const VISIBLE_SCREENING_STATUSES = ['announced', 'wildcard', 'live'];
export const isVisibleStatus = (status) => VISIBLE_SCREENING_STATUSES.includes(status);
/**
 * Who is hyped for a screening.
 *
 * The whole list travels, not just a count: the meter shows faces (DESIGN.md
 * §7) and at forty members the array is smaller than the round trip it would
 * take to fetch it separately. `mine` is resolved server-side from the session
 * so no client has to work out which of these is itself.
 */
export const hypeMemberSchema = memberSummarySchema;
export const hypeSchema = z.object({
    count: z.number().int().nonnegative(),
    mine: z.boolean(),
    who: z.array(hypeMemberSchema),
});
/**
 * The seats, as the board card needs them.
 *
 * `who` travels for the same reason `hype.who` does: the card shows faces, and
 * at forty members the array is smaller than the round trip it would take to
 * fetch it separately. It is also the more interesting of the two lists — hype
 * is who fancies the film, this is who is actually coming.
 *
 * `mine` is resolved server-side, so the button can say "promeni mesto"
 * without the client having to find itself in an array.
 */
export const seatCountsSchema = z.object({
    /** Bookable seats in the room. Zero means the room has no seat map yet. */
    total: z.number().int().nonnegative(),
    free: z.number().int().nonnegative(),
    mine: z.boolean(),
    who: z.array(memberSummarySchema),
});
/**
 * What the corkboard sent to a wildcard slot (DESIGN.md §12).
 *
 * The winning pitch travels with the card even once the film is bound, because
 * "Miloš je ovo predložio" is the better half of the joke and it is gone the
 * moment the poster appears. `filled` is false when the winner was a free title
 * with no film row behind it: the vote has an answer, the slot still has no
 * film, and the card should say exactly that rather than pretend either way.
 */
export const pitchWinnerSchema = z.object({
    pitchId: z.string().uuid(),
    /**
     * The winning film's three names, or null when the card carried no film.
     *
     * The server used to flatten this to one string here, and it cannot any more:
     * which of the three a person wants to read is a property of the *reader*,
     * and this payload is cached, broadcast and shown to everybody at once. So
     * the row travels and `winnerTitle()` spends it on the client.
     */
    titles: titleSetSchema.nullable(),
    /** What the author typed, when the card carried no film. Never translated. */
    freeTitle: z.string().nullable(),
    text: z.string(),
    by: memberSummarySchema,
    filled: z.boolean(),
});
/** The winning card's name, in the reader's order. Mirrors `pitchTitle`. */
export function winnerTitle(winner, order = DEFAULT_TITLE_ORDER) {
    if (winner.titles)
        return movieTitle(winner.titles, order);
    return winner.freeTitle ?? NO_TITLE;
}
export const boardScreeningSchema = z.object({
    id: z.string().uuid(),
    status: screeningStatusSchema,
    /** ISO 8601, UTC. The client formats it in the viewer's own timezone. */
    startsAt: z.string(),
    taglineSr: z.string().nullable(),
    pitchClosesAt: z.string().nullable(),
    /**
     * When the standings were actually read. Null and a past `pitchClosesAt`
     * together mean the resolver has not run yet — a state that exists for
     * seconds normally and for as long as the API is down otherwise, which is
     * why the card reads this rather than comparing the close time to the clock.
     */
    pitchResolvedAt: z.string().nullable(),
    /** Null until the vote closes; still null afterwards if nobody pitched. */
    pitchWinner: pitchWinnerSchema.nullable(),
    /** Null is a wildcard slot: the film is still being voted on. */
    movie: movieSchema.nullable(),
    room: roomSummarySchema,
    seats: seatCountsSchema,
    hype: hypeSchema,
});
/**
 * `now` travels with the board so the client's "za 2 dana" and its drop-off
 * arithmetic agree with the server's, whatever the phone's clock says.
 */
export const boardResponse = z.object({
    now: z.string(),
    screenings: z.array(boardScreeningSchema),
});
// --- admin ------------------------------------------------------------------
/**
 * A wildcard slot is exactly "a screening with no movie yet", and the two must
 * not be able to disagree.
 *
 * This can only judge what the request actually says. A patch of
 * `{status: 'announced'}` against a draft that already has a film is perfectly
 * valid, and an earlier version of this rule rejected it — it read the absent
 * `movieId` as "no film" and refused to publish anything. So the film half of
 * the rule applies only when the request states the film; when it does not, the
 * route re-checks the same rule against the merged row, which is the only place
 * that knows what the screening already had.
 */
const wildcardCoherent = (value, ctx) => {
    if (value.status === undefined)
        return;
    const wildcard = value.status === 'wildcard';
    // Presence, not truthiness: `movieId: null` is a statement ("take the film
    // off"), whereas an absent key is silence.
    const stated = 'movieId' in value;
    const hasMovie = stated && value.movieId !== null && value.movieId !== undefined;
    if (wildcard && hasMovie) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['movieId'],
            message: 'Wildcard termin nema film — film bira glasanje.',
        });
    }
    // A draft may be undecided and a cancelled night no longer has to make sense.
    const needsFilm = !wildcard && value.status !== 'draft' && value.status !== 'cancelled';
    if (needsFilm && stated && !hasMovie) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['movieId'],
            message: 'Termin bez filma može biti samo nacrt ili wildcard.',
        });
    }
};
export const createScreeningRequest = z
    .object({
    movieId: z.string().uuid().nullable().default(null),
    roomId: z.string().uuid(),
    startsAt: z.string().datetime({ offset: true }),
    /** 'draft' is invisible on the board; 'announced' is the real publish. */
    status: z.enum(['draft', 'announced', 'wildcard']).default('draft'),
    taglineSr: z.string().trim().max(200).nullable().default(null),
    pitchClosesAt: z.string().datetime({ offset: true }).nullable().default(null),
})
    .superRefine(wildcardCoherent);
export const updateScreeningRequest = z
    .object({
    movieId: z.string().uuid().nullable(),
    roomId: z.string().uuid(),
    startsAt: z.string().datetime({ offset: true }),
    status: screeningStatusSchema,
    taglineSr: z.string().trim().max(200).nullable(),
    pitchClosesAt: z.string().datetime({ offset: true }).nullable(),
})
    .partial()
    .superRefine(wildcardCoherent);
export const createRoomRequest = z.object({
    name: z.string().trim().min(2, 'Bar 2 karaktera.').max(60, 'Najviše 60 karaktera.'),
    notes: z.string().trim().max(500).nullable().default(null),
    /**
     * Co-host rights are scoped to the rooms you own (DESIGN.md §2). Omitted
     * means you keep it yourself.
     */
    ownerId: z.string().uuid().nullable().default(null),
});
/**
 * When a screening drops off the board.
 *
 * The server's `WHERE` clause is the authority — it decides which rows travel
 * at all. This exists so the client can answer a different question with the
 * same rule: *when should I ask again?* A card whose night has ended while
 * somebody was looking at the page should leave, and the honest way to make
 * that happen is to refetch at the moment it becomes stale rather than to
 * filter locally and drift from what the server would have sent.
 */
export function screeningEndsAt(startsAt, runtimeMin) {
    const at = typeof startsAt === 'string' ? new Date(startsAt) : startsAt;
    return new Date(at.getTime() + ((runtimeMin ?? ASSUMED_RUNTIME_MIN) + BOARD_GRACE_MIN) * 60_000);
}
export function boardEndsAt(screening) {
    return screeningEndsAt(screening.startsAt, screening.movie?.runtimeMin);
}
export function wildcardPhase(screening, now = new Date()) {
    if (screening.movie !== null || screening.status !== 'wildcard')
        return null;
    if (screening.pitchResolvedAt === null) {
        const closes = screening.pitchClosesAt;
        if (closes !== null && Date.parse(closes) <= now.getTime())
            return 'counting';
        return 'voting';
    }
    return screening.pitchWinner === null ? 'empty' : 'chosen';
}
/**
 * The hype toggle's answer. `count` is the server's, and it is what the client
 * shows — an optimistic +1 that turns out to be wrong (two people tapping at
 * once, or a stale page) is corrected by this rather than by a refetch.
 */
export const hypeToggleResponse = z.object({
    hyped: z.boolean(),
    count: z.number().int().nonnegative(),
});
//# sourceMappingURL=screenings.js.map